import { useState } from 'react';
import { Loader2, Landmark } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

export function BankAccountInventoryPanel() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.bankAccountInventory();
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Landmark className="h-4 w-4" />
          Bank Account Inventory
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Every bank account (including hidden) with source, entity, currency, last transaction
          date, transaction count, and feed status.
        </p>

        <Button onClick={run} disabled={loading} size="sm" data-testid="bank-account-inventory-run">
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          Run
        </Button>

        {result && (
          <div className="space-y-3" data-testid="bank-account-inventory-result">
            <div className="flex flex-wrap gap-4 text-sm">
              <span>Total accounts: <strong>{result.total_accounts}</strong> (expected ~{result.expected_approx})</span>
              {result.accounts_without_entity > 0 && (
                <Badge variant="outline" className="text-amber-700 dark:text-amber-400">
                  {result.accounts_without_entity} without entity
                </Badge>
              )}
              {result.accounts_without_feed > 0 && (
                <Badge variant="outline" className="text-amber-700 dark:text-amber-400">
                  {result.accounts_without_feed} without feed
                </Badge>
              )}
            </div>

            <div className="overflow-x-auto border rounded-lg">
              <table className="w-full text-xs" data-testid="bank-account-inventory-table">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left px-2 py-1.5 font-medium">Account</th>
                    <th className="text-left px-2 py-1.5 font-medium">Entity</th>
                    <th className="text-left px-2 py-1.5 font-medium">Currency</th>
                    <th className="text-left px-2 py-1.5 font-medium">Source</th>
                    <th className="text-right px-2 py-1.5 font-medium">Txns</th>
                    <th className="text-left px-2 py-1.5 font-medium">Last Txn</th>
                  </tr>
                </thead>
                <tbody>
                  {result.accounts?.map((a) => (
                    <tr key={a.id} className="border-b last:border-0">
                      <td className="px-2 py-1 font-mono">{a.bank_name || a.account_name || a.id}</td>
                      <td className="px-2 py-1">
                        {a.has_entity ? a.entity_name : <span className="text-amber-600">unassigned</span>}
                      </td>
                      <td className="px-2 py-1">{a.currency}</td>
                      <td className="px-2 py-1">
                        {a.has_feed ? a.source : <span className="text-amber-600">{a.source}</span>}
                      </td>
                      <td className="px-2 py-1 text-right tabular-nums">{a.transaction_count}</td>
                      <td className="px-2 py-1 whitespace-nowrap">{(a.last_transaction_date || '').slice(0, 10) || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
